import React, { useEffect, useRef, useState } from "react";
import { Button, Form, Table } from "react-bootstrap";
import { FaArrowLeft, FaPencilAlt, FaTimes, FaTrash } from "react-icons/fa";
import { AccountStage, AccountStageOrder } from "../../constants/accountStages";
import CustomerAccountService, {
  buildAddActivityTimelinePayload,
  buildAddTransactionPayload,
  formatTotalAmount,
  parseActivityListResponse,
  parseTransactionListResponse,
} from "../../services/CustomerAccountService";

const TX_PAGE_SIZE = 5;
const ACTIVITY_PAGE_SIZE = 5;

const PAYMENT_METHOD_OPTIONS = [
  "Bank Transfer",
  "UPI",
  "Credit Card",
  "Cash",
  "Net Banking",
  "Other",
];

function formatAmountDisplay(amount) {
  if (typeof amount === "string" && amount.includes("₹")) return amount;
  const n =
    typeof amount === "number"
      ? amount
      : Number(String(amount ?? "").replace(/[^0-9.]/g, ""));
  return Number.isFinite(n) && n > 0 ? `₹${n.toLocaleString("en-IN")}` : "—";
}

function parseAmountInput(value) {
  const n = Number(String(value ?? "").replace(/[^0-9.]/g, ""));
  return Number.isFinite(n) ? n : 0;
}

function defaultDraftDateInput() {
  return new Date().toISOString().slice(0, 10);
}

function dateToInputValue(value) {
  if (!value || value === "—") return defaultDraftDateInput();
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(value).trim())) {
    return String(value).trim().slice(0, 10);
  }
  const d = new Date(value);
  if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  const m = String(value).match(/(\d{1,2})\s+(\w+)\s+(\d{2,4})/);
  if (m) {
    const day = m[1].padStart(2, "0");
    const year = m[3].length === 2 ? `20${m[3]}` : m[3];
    const monthIdx = new Date(`${m[2]} 1, 2000`).getMonth();
    return `${year}-${String(monthIdx + 1).padStart(2, "0")}-${day}`;
  }
  return defaultDraftDateInput();
}

function dateInputToIso(value) {
  if (!value) return new Date().toISOString();
  const d = new Date(`${value}T00:00:00`);
  return Number.isNaN(d.getTime()) ? new Date().toISOString() : d.toISOString();
}

function emptyTransactionDraft() {
  return {
    description: "",
    amount: "0",
    date: defaultDraftDateInput(),
    method: PAYMENT_METHOD_OPTIONS[0],
    note: "",
    receiptNo: "",
  };
}

function emptyActivityDraft() {
  return {
    activityDate: defaultDraftDateInput(),
    title: "",
    description: "",
    amount: "",
  };
}

function normalizeTransaction(row, index = 0) {
  const amountNum =
    typeof row.amount === "number"
      ? row.amount
      : parseAmountInput(row.amount);
  return {
    id: row.id ?? `tx-${index}`,
    description: row.description ?? row.Description ?? "—",
    amount: amountNum,
    amountDisplay: formatAmountDisplay(row.amount ?? amountNum),
    date: row.date ?? row.Date ?? "—",
    method: row.method ?? row.paymentMethod ?? row.PaymentMethod ?? "—",
    note: row.note ?? row.notes ?? row.Notes ?? "—",
    receiptNo: row.receiptNo ?? "",
  };
}

function formatSinceDate(joined) {
  if (!joined || joined === "—") return "Jan 2026";
  const d = new Date(joined);
  if (!Number.isNaN(d.getTime())) {
    return d.toLocaleDateString("en-GB", { month: "short", year: "numeric" });
  }
  const m = String(joined).match(/([A-Za-z]+)\s+\d{4}/);
  return m ? m[0] : joined;
}

const labelStyle = { color: "#6c757d", fontSize: "0.8rem", marginBottom: 2 };
const valueStyle = { fontSize: "0.9rem", marginBottom: 10 };
const inputCellStyle = { padding: "6px 4px", verticalAlign: "middle" };
const compactControl = { fontSize: "0.8rem", minWidth: 0 };

function TransactionFormRow({
  draft,
  onUpdateDraft,
  onSave,
  onCancel,
  saving,
}) {
  return (
    <tr style={{ background: "#fafbfc" }}>
      <td style={inputCellStyle}>
        <Form.Control
          size="sm"
          placeholder="Description"
          value={draft.description}
          onChange={(e) => onUpdateDraft("description", e.target.value)}
          style={compactControl}
        />
      </td>
      <td style={inputCellStyle}>
        <Form.Control
          size="sm"
          placeholder="₹0"
          value={draft.amount}
          onChange={(e) => onUpdateDraft("amount", e.target.value)}
          style={{ ...compactControl, width: 88 }}
        />
      </td>
      <td style={inputCellStyle}>
        <Form.Control
          size="sm"
          type="date"
          value={draft.date}
          onChange={(e) => onUpdateDraft("date", e.target.value)}
          style={{ ...compactControl, width: 130 }}
        />
      </td>
      <td style={inputCellStyle}>
        <Form.Select
          size="sm"
          value={draft.method}
          onChange={(e) => onUpdateDraft("method", e.target.value)}
          style={{ ...compactControl, minWidth: 110 }}
        >
          {PAYMENT_METHOD_OPTIONS.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </Form.Select>
      </td>
      <td style={inputCellStyle}>
        <Form.Control
          size="sm"
          placeholder="Notes"
          value={draft.note}
          onChange={(e) => onUpdateDraft("note", e.target.value)}
          style={{ ...compactControl, width: 100 }}
        />
      </td>
      <td style={inputCellStyle}>
        <Form.Control
          size="sm"
          placeholder="Receipt No."
          value={draft.receiptNo}
          onChange={(e) => onUpdateDraft("receiptNo", e.target.value)}
          style={{ ...compactControl, width: 100 }}
        />
      </td>
      <td style={{ ...inputCellStyle, whiteSpace: "nowrap" }} className="text-end">
        <Button
          size="sm"
          variant="success"
          className="me-1 py-0 px-2"
          style={{ fontSize: "0.75rem" }}
          onClick={onSave}
          disabled={
            saving ||
            !draft.description.trim() ||
            !parseAmountInput(draft.amount) ||
            !draft.date.trim()
          }
        >
          {saving ? "Saving…" : "Save"}
        </Button>
        <Button
          size="sm"
          variant="light"
          className="py-0 px-2 border"
          style={{ fontSize: "0.75rem" }}
          onClick={onCancel}
        >
          Cancel
        </Button>
      </td>
    </tr>
  );
}

function SectionFeedback({ feedback }) {
  if (!feedback?.text) return null;
  return (
    <span
      className="fw-normal ms-2"
      style={{
        fontSize: "0.7rem",
        color: feedback.type === "success" ? "#198754" : "#dc3545",
      }}
    >
      {feedback.text}
    </span>
  );
}

function SectionStatus({ loading, updating }) {
  if (updating) {
    return (
      <span className="text-muted fw-normal ms-2" style={{ fontSize: "0.7rem" }}>
        Updating…
      </span>
    );
  }
  if (loading) {
    return (
      <span className="text-muted fw-normal ms-2" style={{ fontSize: "0.7rem" }}>
        Loading…
      </span>
    );
  }
  return null;
}

function ActivityFormRow({
  draft,
  onUpdateDraft,
  onSave,
  onCancel,
  saving,
}) {
  return (
    <tr style={{ background: "#fafbfc" }}>
      <td style={inputCellStyle}>
        <Form.Control
          size="sm"
          type="date"
          value={draft.activityDate}
          onChange={(e) => onUpdateDraft("activityDate", e.target.value)}
          style={{ ...compactControl, width: 130 }}
        />
      </td>
      <td style={inputCellStyle}>
        <Form.Control
          size="sm"
          placeholder="Title"
          value={draft.title}
          onChange={(e) => onUpdateDraft("title", e.target.value)}
          style={compactControl}
        />
      </td>
      <td style={inputCellStyle}>
        <Form.Control
          size="sm"
          placeholder="Amount"
          value={draft.amount}
          onChange={(e) => onUpdateDraft("amount", e.target.value)}
          style={{ ...compactControl, width: 88 }}
        />
      </td>
      <td style={inputCellStyle}>
        <Form.Control
          size="sm"
          placeholder="Description"
          value={draft.description}
          onChange={(e) => onUpdateDraft("description", e.target.value)}
          style={compactControl}
        />
      </td>
      <td style={{ ...inputCellStyle, whiteSpace: "nowrap" }} className="text-end">
        <Button
          size="sm"
          variant="success"
          className="me-1 py-0 px-2"
          style={{ fontSize: "0.75rem" }}
          onClick={onSave}
          disabled={saving || !draft.title.trim()}
        >
          {saving ? "Saving…" : "Save"}
        </Button>
        <Button
          size="sm"
          variant="light"
          className="py-0 px-2 border"
          style={{ fontSize: "0.75rem" }}
          onClick={onCancel}
          disabled={saving}
        >
          Cancel
        </Button>
      </td>
    </tr>
  );
}

const AccountDetailPanel = ({ open, account, onClose, onStageChange, onAccountUpdated }) => {
  const [stageLocal, setStageLocal] = useState(account?.stage ?? AccountStage.NEW_ACCOUNT);
  const [transactions, setTransactions] = useState([]);
  const [activities, setActivities] = useState([]);
  const transactionsRef = useRef(transactions);
  const activitiesRef = useRef(activities);
  const [txLoading, setTxLoading] = useState(false);
  const [txUpdating, setTxUpdating] = useState(false);
  const [txPageNumber, setTxPageNumber] = useState(1);
  const [txPageSize] = useState(TX_PAGE_SIZE);
  const [txTotalCount, setTxTotalCount] = useState(0);
  const [txTotalAmount, setTxTotalAmount] = useState(0);
  const [txSaving, setTxSaving] = useState(false);
  const [isAddingTransaction, setIsAddingTransaction] = useState(false);
  const [editingTransactionId, setEditingTransactionId] = useState(null);
  const [transactionDraft, setTransactionDraft] = useState(emptyTransactionDraft);
  const [isAddingActivity, setIsAddingActivity] = useState(false);
  const [activitySaving, setActivitySaving] = useState(false);
  const [activityDraft, setActivityDraft] = useState(emptyActivityDraft);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityUpdating, setActivityUpdating] = useState(false);
  const [activityPageNumber, setActivityPageNumber] = useState(1);
  const [activityPageSize] = useState(ACTIVITY_PAGE_SIZE);
  const [activityTotalCount, setActivityTotalCount] = useState(0);
  const [txFeedback, setTxFeedback] = useState(null);
  const [activityFeedback, setActivityFeedback] = useState(null);
  const txFeedbackTimer = useRef(null);
  const activityFeedbackTimer = useRef(null);

  useEffect(() => {
    transactionsRef.current = transactions;
  }, [transactions]);

  useEffect(() => {
    activitiesRef.current = activities;
  }, [activities]);

  useEffect(
    () => () => {
      clearTimeout(txFeedbackTimer.current);
      clearTimeout(activityFeedbackTimer.current);
    },
    [],
  );

  const showTxFeedback = (type, text) => {
    clearTimeout(txFeedbackTimer.current);
    setTxFeedback({ type, text });
    txFeedbackTimer.current = setTimeout(() => setTxFeedback(null), 4000);
  };

  const showActivityFeedback = (type, text) => {
    clearTimeout(activityFeedbackTimer.current);
    setActivityFeedback({ type, text });
    activityFeedbackTimer.current = setTimeout(() => setActivityFeedback(null), 4000);
  };

  const emptyTxSummary = () => ({
    transactions: [],
    totalCount: 0,
    totalAmount: 0,
    pageNumber: 1,
  });

  const loadActivities = async (
    accountId,
    page = 1,
    size = activityPageSize,
    { soft = false } = {},
  ) => {
    if (!accountId) {
      setActivities([]);
      setActivityTotalCount(0);
      setActivityPageNumber(1);
      return { activities: [], totalCount: 0, pageNumber: 1 };
    }

    const keepPrevious = soft || activitiesRef.current.length > 0;
    if (keepPrevious) setActivityUpdating(true);
    else setActivityLoading(true);
    try {
      const resp = await CustomerAccountService.getActivityTimelines(accountId, {
        pageNumber: page,
        pageSize: size,
      });
      const parsed = parseActivityListResponse(resp);
      setActivities(parsed.activities);
      setActivityTotalCount(parsed.totalCount);
      setActivityPageNumber(parsed.pageNumber);
      return parsed;
    } catch (err) {
      console.error(err);
      if (!keepPrevious) {
        setActivities([]);
        setActivityTotalCount(0);
      }
      return { activities: [], totalCount: 0, pageNumber: page };
    } finally {
      if (keepPrevious) setActivityUpdating(false);
      else setActivityLoading(false);
    }
  };

  const loadTransactions = async (
    leadId,
    page = 1,
    size = txPageSize,
    { soft = false } = {},
  ) => {
    if (!leadId) {
      setTransactions([]);
      setTxTotalCount(0);
      setTxTotalAmount(0);
      setTxPageNumber(1);
      return emptyTxSummary();
    }

    const keepPrevious = soft || transactionsRef.current.length > 0;
    if (keepPrevious) setTxUpdating(true);
    else setTxLoading(true);
    try {
      const resp = await CustomerAccountService.getTransactions(leadId, {
        pageNumber: page,
        pageSize: size,
      });
      const parsed = parseTransactionListResponse(resp);
      setTransactions(parsed.transactions);
      setTxTotalCount(parsed.totalCount);
      setTxPageNumber(parsed.pageNumber);
      setTxTotalAmount(parsed.totalAmount);
      return parsed;
    } catch (err) {
      console.error(err);
      if (!keepPrevious) {
        setTransactions([]);
        setTxTotalCount(0);
        setTxTotalAmount(0);
      }
      return emptyTxSummary();
    } finally {
      if (keepPrevious) setTxUpdating(false);
      else setTxLoading(false);
    }
  };

  const syncAccountCashInflow = (totalAmount) => {
    if (!account?.accountId) return;
    const amount = Number(totalAmount) || 0;
    onAccountUpdated?.({
      accountId: account.accountId,
      cashInflow: formatTotalAmount(amount),
      revenue: amount > 0 ? String(amount) : "—",
    });
  };

  const loadInitialSections = async (acc) => {
    if (!acc?.accountId) return;
    const accountLeadId = acc?.leadId || acc?.linkedLeadId;
    await Promise.allSettled([
      loadActivities(acc.accountId, 1, activityPageSize),
      accountLeadId
        ? loadTransactions(accountLeadId, 1, txPageSize)
        : Promise.resolve(emptyTxSummary()),
    ]);
  };

  const handleTxPageChange = async (nextPage) => {
    const accountLeadId = account?.leadId || account?.linkedLeadId;
    if (!accountLeadId || nextPage < 1) return;
    const totalPages = Math.max(1, Math.ceil(txTotalCount / txPageSize));
    if (nextPage > totalPages) return;
    setIsAddingTransaction(false);
    setEditingTransactionId(null);
    await loadTransactions(accountLeadId, nextPage, txPageSize, { soft: true });
  };

  const handleActivityPageChange = async (nextPage) => {
    if (!account?.accountId || nextPage < 1) return;
    const totalPages = Math.max(1, Math.ceil(activityTotalCount / activityPageSize));
    if (nextPage > totalPages) return;
    setIsAddingActivity(false);
    await loadActivities(account.accountId, nextPage, activityPageSize, { soft: true });
  };

  const accountId = account?.accountId;

  useEffect(() => {
    if (!accountId || !open) return;
    setStageLocal(account.stage ?? AccountStage.NEW_ACCOUNT);
    setIsAddingTransaction(false);
    setEditingTransactionId(null);
    setIsAddingActivity(false);
    setActivitySaving(false);
    setActivityDraft(emptyActivityDraft());
    setTransactions([]);
    setActivities([]);
    setTxPageNumber(1);
    setTxTotalCount(0);
    setTxTotalAmount(0);
    setActivityPageNumber(1);
    setActivityTotalCount(0);
    setTxLoading(false);
    setTxUpdating(false);
    setActivityLoading(false);
    setActivityUpdating(false);
    setTxFeedback(null);
    setActivityFeedback(null);
    loadInitialSections(account);
  }, [accountId, open]);

  const txTotalPages = Math.max(1, Math.ceil(txTotalCount / txPageSize));
  const txPageStart = txTotalCount === 0 ? 0 : (txPageNumber - 1) * txPageSize + 1;
  const txPageEnd = Math.min(txPageNumber * txPageSize, txTotalCount);
  const activityTotalPages = Math.max(1, Math.ceil(activityTotalCount / activityPageSize));
  const activityPageStart =
    activityTotalCount === 0 ? 0 : (activityPageNumber - 1) * activityPageSize + 1;
  const activityPageEnd = Math.min(activityPageNumber * activityPageSize, activityTotalCount);
  const totalValue = formatTotalAmount(txTotalAmount);

  if (!open || !account) return null;

  const displayContact = account.contactName || "—";
  const displayEmail = account.email || "—";
  const displayPhone = account.phone || "—";
  const linkedName = displayContact || "—";
  const sinceLabel = formatSinceDate(account.joined);

  const handleStageChange = (e) => {
    const next = e.target.value;
    setStageLocal(next);
    onStageChange?.(account.accountId, next);
  };

  const handleDeleteTransaction = async (id) => {
    if (!window.confirm("Delete this transaction? This action cannot be undone.")) {
      return;
    }

    try {
      await CustomerAccountService.deleteTransaction(id);
      if (editingTransactionId === id) {
        setEditingTransactionId(null);
        setIsAddingTransaction(false);
      }
      const accountLeadId = account?.leadId || account?.linkedLeadId;
      const nextPage =
        transactions.length === 1 && txPageNumber > 1 ? txPageNumber - 1 : txPageNumber;
      if (accountLeadId) {
        const parsed = await loadTransactions(accountLeadId, nextPage, txPageSize, {
          soft: true,
        });
        syncAccountCashInflow(parsed.totalAmount);
      }
      showTxFeedback("success", "Transaction deleted successfully.");
    } catch (err) {
      console.error(err);
      showTxFeedback(
        "error",
        err?.response?.data?.message || err?.message || "Failed to delete transaction.",
      );
    }
  };

  const startAddTransaction = () => {
    setEditingTransactionId(null);
    setTransactionDraft(emptyTransactionDraft());
    setIsAddingTransaction(true);
  };

  const startEditTransaction = (row) => {
    setIsAddingTransaction(false);
    setEditingTransactionId(row.id);
    setTransactionDraft({
      description: row.description === "—" ? "" : row.description,
      amount: row.amount ? String(row.amount) : "",
      date: row.date === "—" ? defaultDraftDateInput() : dateToInputValue(row.date),
      method: row.method === "—" ? PAYMENT_METHOD_OPTIONS[0] : row.method,
      note: row.note === "—" ? "" : row.note,
      receiptNo: row.receiptNo && row.receiptNo !== "—" ? row.receiptNo : "",
    });
  };

  const cancelTransactionForm = () => {
    setIsAddingTransaction(false);
    setEditingTransactionId(null);
    setTransactionDraft(emptyTransactionDraft());
  };

  const saveTransactionForm = async () => {
    const description = transactionDraft.description.trim();
    const amountNum = parseAmountInput(transactionDraft.amount);
    const date = transactionDraft.date.trim();
    const method = transactionDraft.method;
    const note = transactionDraft.note.trim();
    const receiptNo = transactionDraft.receiptNo.trim();

    if (!description || !amountNum || !date || !account) return;

    setTxSaving(true);
    try {
      const txId = editingTransactionId ?? crypto.randomUUID?.() ?? `tx-${Date.now()}`;
      const payload = buildAddTransactionPayload({
        account,
        transactionId: txId,
        description,
        amount: amountNum,
        paymentMethod: method,
        notes: note,
        receiptNo,
        paidOn: dateInputToIso(date),
      });

      const resp = editingTransactionId
        ? await CustomerAccountService.updateTransaction(payload)
        : await CustomerAccountService.addTransaction(payload);

      const ok = resp?.data?.success !== false;
      if (!ok) {
        throw new Error(resp?.data?.message || "Failed to save transaction.");
      }

      const wasEditing = !!editingTransactionId;
      cancelTransactionForm();
      const accountLeadId = account?.leadId || account?.linkedLeadId;
      const reloadPage = wasEditing ? txPageNumber : 1;
      if (accountLeadId) {
        const parsed = await loadTransactions(accountLeadId, reloadPage, txPageSize, {
          soft: true,
        });
        syncAccountCashInflow(parsed.totalAmount);
      }
      showTxFeedback(
        "success",
        wasEditing ? "Transaction updated successfully." : "Transaction added successfully.",
      );
    } catch (err) {
      console.error(err);
      showTxFeedback(
        "error",
        err?.response?.data?.message || err?.message || "Failed to save transaction.",
      );
    } finally {
      setTxSaving(false);
    }
  };

  const updateDraft = (field, value) => {
    setTransactionDraft((prev) => ({ ...prev, [field]: value }));
  };

  const updateActivityDraft = (field, value) => {
    setActivityDraft((prev) => ({ ...prev, [field]: value }));
  };

  const startAddActivity = () => {
    setActivityDraft(emptyActivityDraft());
    setIsAddingActivity(true);
  };

  const cancelAddActivity = () => {
    setIsAddingActivity(false);
    setActivityDraft(emptyActivityDraft());
  };

  const saveActivity = async () => {
    const title = activityDraft.title.trim();
    if (!title || !account?.accountId) return;

    setActivitySaving(true);
    try {
      const amountNum = parseAmountInput(activityDraft.amount);
      const activityDate = dateInputToIso(activityDraft.activityDate);

      const resp = await CustomerAccountService.createActivityTimeline(
        buildAddActivityTimelinePayload({
          customerAccountId: account.accountId,
          activityDate,
          title,
          description: activityDraft.description.trim(),
          amount: amountNum,
        }),
      );

      const ok = resp?.data?.success !== false;
      if (!ok) {
        throw new Error(resp?.data?.message || "Failed to add activity.");
      }

      cancelAddActivity();
      await loadActivities(account.accountId, 1, activityPageSize, { soft: true });
      showActivityFeedback("success", "Activity added successfully.");
    } catch (err) {
      console.error(err);
      showActivityFeedback(
        "error",
        err?.response?.data?.message || err?.message || "Failed to add activity.",
      );
    } finally {
      setActivitySaving(false);
    }
  };

  return (
    <div
      className="account-detail-overlay"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1060,
        display: "flex",
        fontFamily: "'Inria Serif', Georgia, serif",
      }}
      role="presentation"
    >
      <div
        style={{ flex: 1, background: "rgba(0,0,0,0.35)" }}
        onClick={onClose}
        aria-hidden
      />
      <div
        className="account-detail-panel shadow-lg"
        style={{
          width: "min(900px, 100vw)",
          maxWidth: "100%",
          background: "#fff",
          height: "100%",
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="account-detail-title"
      >
        <div
          className="d-flex align-items-center border-bottom px-3 py-2 gap-2"
          style={{ position: "sticky", top: 0, background: "#fff", zIndex: 2 }}
        >
          <Button
            variant="link"
            className="p-0 text-decoration-none text-dark d-flex align-items-center gap-1"
            onClick={onClose}
          >
            <FaArrowLeft size={14} />
            <span>Back</span>
          </Button>
          <div
            id="account-detail-title"
            className="fw-bold flex-grow-1 text-center text-truncate px-2"
            style={{ fontSize: "1.05rem" }}
          >
            {account.company}
          </div>
          <div className="d-flex align-items-center gap-2 flex-shrink-0">
            <button
              type="button"
              className="d-inline-flex align-items-center justify-content-center border-0 rounded-circle flex-shrink-0"
              style={{
                width: 28,
                height: 28,
                background: "#e9ecef",
                color: "#495057",
                fontSize: "0.8rem",
                lineHeight: 1,
              }}
              onClick={onClose}
              aria-label="Close"
            >
              <FaTimes />
            </button>
          </div>
        </div>

        <div className="p-3 flex-grow-1">
          <div className="row g-3 mb-4">
            <div className="col-6">
              <div style={{ fontSize: "1.75rem", fontWeight: 700, lineHeight: 1.2 }}>
                {totalValue}
              </div>
              <div style={labelStyle}>Total Value</div>
              <div className="fw-semibold mt-2" style={{ fontSize: "0.95rem" }}>
                {txTotalCount} Transactions
              </div>
              <div
                className="mt-2 fw-semibold"
                style={{ color: "#198754", fontSize: "0.95rem" }}
              >
                {stageLocal}
              </div>
              <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                Since {sinceLabel}
              </div>
            </div>

            <div className="col-6">
              <div style={labelStyle}>Contact</div>
              <div style={valueStyle}>{displayContact}</div>
              <div style={labelStyle}>Email</div>
              <div style={valueStyle}>{displayEmail}</div>
              <div style={labelStyle}>Phone</div>
              <div style={valueStyle}>{displayPhone}</div>
              <div style={labelStyle}>Source</div>
              <div style={valueStyle}>
                {account.source && account.source !== "-" ? account.source : "Qualified Lead"}
              </div>
              <div style={labelStyle}>Stage</div>
              <Form.Select
                size="sm"
                className="mb-2"
                value={stageLocal}
                onChange={handleStageChange}
              >
                {AccountStageOrder.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Form.Select>
              <div style={labelStyle}>Linked</div>
              <Button
                variant="link"
                className="p-0 text-decoration-none"
                style={{ color: "#0d6efd", fontSize: "0.9rem" }}
              >
                [{linkedName} →]
              </Button>
              <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                (original lead)
              </div>
            </div>
          </div>

          <div className="d-flex align-items-center justify-content-between mb-2">
            <span
              className="fw-bold text-secondary"
              style={{ fontSize: "0.72rem", letterSpacing: "0.08em" }}
            >
              TRANSACTIONS
              <SectionStatus
                loading={txLoading && transactions.length === 0}
                updating={txUpdating || (txLoading && transactions.length > 0)}
              />
              <SectionFeedback feedback={txFeedback} />
            </span>
            <Button
              variant="link"
              className="p-0 text-decoration-none"
              style={{ color: "#0d6efd", fontSize: "0.85rem" }}
              onClick={startAddTransaction}
              disabled={isAddingTransaction || txSaving }
              title={
                account?.leadId || account?.linkedLeadId
                  ? "Add a transaction"
                  : "Link a lead to this account to add transactions"
              }
            >
              [+ Add Transaction]
            </Button>
          </div>

          <div
            className="table-responsive border rounded mb-2"
            style={{ overflowX: "auto" }}
          >
            <Table size="sm" className="mb-0 align-middle">
              <thead style={{ background: "#f1f3f5" }}>
                <tr>
                  {["DESCRIPTION", "AMOUNT", "DATE", "PAYMENT METHOD", "NOTES", "RECEIPT NO."].map((col) => (
                    <th
                      key={col}
                      className="text-secondary fw-semibold"
                      style={{ fontSize: "0.7rem", whiteSpace: "nowrap" }}
                    >
                      {col}
                    </th>
                  ))}
                  <th style={{ width: 120 }} aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {!txLoading && !txUpdating && transactions.length === 0 && !isAddingTransaction ? (
                  <tr>
                    <td colSpan={7} className="text-muted small text-center py-3">
                      No transactions yet.
                    </td>
                  </tr>
                ) : null}
                {transactions.map((row) => {
                  if (editingTransactionId === row.id) {
                    return (
                      <TransactionFormRow
                        key={`edit-${row.id}`}
                        draft={transactionDraft}
                        onUpdateDraft={updateDraft}
                        onSave={saveTransactionForm}
                        onCancel={cancelTransactionForm}
                        saving={txSaving}
                      />
                    );
                  }
                  return (
                    <tr key={row.id}>
                      <td style={{ fontSize: "0.85rem" }}>{row.description}</td>
                      <td style={{ fontSize: "0.85rem", whiteSpace: "nowrap" }}>
                        {row.amountDisplay}
                      </td>
                      <td style={{ fontSize: "0.85rem", whiteSpace: "nowrap" }}>{row.date}</td>
                      <td style={{ fontSize: "0.85rem", whiteSpace: "nowrap" }}>{row.method}</td>
                      <td style={{ fontSize: "0.85rem", whiteSpace: "nowrap" }}>{row.note}</td>
                      <td style={{ fontSize: "0.85rem", whiteSpace: "nowrap" }}>
                        {row.receiptNo || "—"}
                      </td>
                      <td className="text-end text-nowrap">
                        <button
                          type="button"
                          className="btn btn-link p-0 me-2"
                          style={{ color: "#e67e22" }}
                          title="Edit transaction"
                          aria-label="Edit transaction"
                          onClick={() => startEditTransaction(row)}
                          disabled={isAddingTransaction}
                        >
                          <FaPencilAlt size={14} />
                        </button>
                        <button
                          type="button"
                          className="btn btn-link p-0"
                          style={{ color: "#dc3545" }}
                          title="Delete transaction"
                          aria-label="Delete transaction"
                          onClick={() => handleDeleteTransaction(row.id)}
                        >
                          <FaTrash size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {isAddingTransaction ? (
                  <TransactionFormRow
                    draft={transactionDraft}
                    onUpdateDraft={updateDraft}
                    onSave={saveTransactionForm}
                    onCancel={cancelTransactionForm}
                    saving={txSaving}
                  />
                ) : null}
              </tbody>
            </Table>
          </div>

          {txTotalCount > txPageSize ? (
            <div className="d-flex align-items-center justify-content-between mb-4">
              <span className="text-muted small">
                Showing {txPageStart}-{txPageEnd} of {txTotalCount}
              </span>
              <div className="d-flex gap-2">
                <Button
                  size="sm"
                  variant="light"
                  className="border py-0 px-2"
                  style={{ fontSize: "0.75rem" }}
                  disabled={txPageNumber <= 1 || txLoading || txUpdating}
                  onClick={() => handleTxPageChange(txPageNumber - 1)}
                >
                  Previous
                </Button>
                <Button
                  size="sm"
                  variant="light"
                  className="border py-0 px-2"
                  style={{ fontSize: "0.75rem" }}
                  disabled={txPageNumber >= txTotalPages || txLoading || txUpdating}
                  onClick={() => handleTxPageChange(txPageNumber + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          ) : (
            <div className="mb-4" />
          )}

          <div className="d-flex align-items-center justify-content-between mb-3">
            <div
              className="fw-bold text-secondary"
              style={{ fontSize: "0.72rem", letterSpacing: "0.08em" }}
            >
              ACTIVITY TIMELINE
              <SectionStatus
                loading={activityLoading && activities.length === 0}
                updating={activityUpdating || (activityLoading && activities.length > 0)}
              />
              <SectionFeedback feedback={activityFeedback} />
            </div>
            <Button
              variant="link"
              className="p-0 text-decoration-none"
              style={{ color: "#0d6efd", fontSize: "0.85rem" }}
              onClick={startAddActivity}
              disabled={isAddingActivity || activitySaving}
            >
              [+ Add Activity]
            </Button>
          </div>

          {isAddingActivity ? (
            <div className="table-responsive border rounded mb-3" style={{ overflowX: "auto" }}>
              <Table size="sm" className="mb-0 align-middle">
                <thead style={{ background: "#f1f3f5" }}>
                  <tr>
                    {["DATE", "TITLE", "AMOUNT", "DESCRIPTION"].map((col) => (
                      <th
                        key={col}
                        className="text-secondary fw-semibold"
                        style={{ fontSize: "0.7rem", whiteSpace: "nowrap" }}
                      >
                        {col}
                      </th>
                    ))}
                    <th style={{ width: 120 }} aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  <ActivityFormRow
                    draft={activityDraft}
                    onUpdateDraft={updateActivityDraft}
                    onSave={saveActivity}
                    onCancel={cancelAddActivity}
                    saving={activitySaving}
                  />
                </tbody>
              </Table>
            </div>
          ) : null}

          {!activityLoading && !activityUpdating && activities.length === 0 ? (
            <div className="text-muted small mb-3">No activities yet.</div>
          ) : null}

          {activities.length > 0 ? (
            <div className="position-relative ps-2 pb-4">
              <div
                className="position-absolute"
                style={{
                  width: 2,
                  background: "#ced4da",
                  left: 5,
                  top: 6,
                  bottom: 6,
                }}
              />
              {activities.map((ev) => {
                const dateLabel = ev.dateLabel ?? ev.date ?? "";
                const title = ev.title ?? "";
                return (
                  <div
                    key={ev.id}
                    className="position-relative d-flex gap-2 mb-3 ps-3"
                    style={{ minHeight: 48 }}
                  >
                    <span
                      className="position-absolute rounded-circle bg-danger flex-shrink-0"
                      style={{
                        width: 10,
                        height: 10,
                        left: -2,
                        top: 6,
                        transform: "translateX(-50%)",
                      }}
                    />
                    <div className="flex-grow-1 min-w-0">
                      <div className="fw-semibold" style={{ fontSize: "0.9rem" }}>
                        {dateLabel}
                        {dateLabel && title ? " — " : ""}
                        {title}
                      </div>
                      {ev.detail ? (
                        <div className="text-muted small">{ev.detail}</div>
                      ) : null}
                    </div>
                    {ev.amount ? (
                      <div
                        className="fw-semibold flex-shrink-0"
                        style={{ color: "#198754", fontSize: "0.9rem" }}
                      >
                        {ev.amount}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ) : null}

          {activityTotalCount > activityPageSize ? (
            <div className="d-flex align-items-center justify-content-between pb-4">
              <span className="text-muted small">
                Showing {activityPageStart}-{activityPageEnd} of {activityTotalCount}
              </span>
              <div className="d-flex gap-2">
                <Button
                  size="sm"
                  variant="light"
                  className="border py-0 px-2"
                  style={{ fontSize: "0.75rem" }}
                  disabled={activityPageNumber <= 1 || activityLoading || activityUpdating}
                  onClick={() => handleActivityPageChange(activityPageNumber - 1)}
                >
                  Previous
                </Button>
                <Button
                  size="sm"
                  variant="light"
                  className="border py-0 px-2"
                  style={{ fontSize: "0.75rem" }}
                  disabled={activityPageNumber >= activityTotalPages || activityLoading || activityUpdating}
                  onClick={() => handleActivityPageChange(activityPageNumber + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          ) : (
            <div className="pb-4" />
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountDetailPanel;
